import React from 'react';
import CustomMarkdownRenderer from './CustomMarkdownRenderer';
import '../styles/TAGStructured.css';

const TAGScreeningRenderer = ({ evaluationData }) => {
  // If we have structured data, render structured format
  if (evaluationData && typeof evaluationData === 'object') {
    const { technicalQuestions, behavioralQuestions, evaluationGuidance } = evaluationData;
    
    // Group technical questions by priority
    const groupedTechnicalQuestions = {
      High: technicalQuestions?.filter(q => q.priority === "High") || [],
      Medium: technicalQuestions?.filter(q => q.priority === "Medium") || [],
      Low: technicalQuestions?.filter(q => q.priority === "Low") || [],
      Unspecified: technicalQuestions?.filter(q => !q.priority) || []
    };
    
    // Assign priorities to unspecified questions based on position
    if (groupedTechnicalQuestions.Unspecified.length > 0 && 
        groupedTechnicalQuestions.High.length === 0 && 
        groupedTechnicalQuestions.Medium.length === 0 &&
        groupedTechnicalQuestions.Low.length === 0) {
      
      const totalQuestions = groupedTechnicalQuestions.Unspecified.length;
      const highCount = Math.ceil(totalQuestions * 0.4);
      const mediumCount = Math.ceil(totalQuestions * 0.3);
      
      groupedTechnicalQuestions.Unspecified.forEach((question, index) => {
        if (index < highCount) {
          groupedTechnicalQuestions.High.push({...question, priority: "High"});
        } else if (index < highCount + mediumCount) {
          groupedTechnicalQuestions.Medium.push({...question, priority: "Medium"});
        } else {
          groupedTechnicalQuestions.Low.push({...question, priority: "Low"});
        }
      });
      
      // Clear the unspecified array after reassigning
      groupedTechnicalQuestions.Unspecified = [];
    }
    
    return (
      <div className="tag-structured-result">
        {/* Technical Questions Section */}
        <section className="question-section technical-questions">
          <h2>Technical Interview Questions</h2>
          
          {/* High Priority Questions */}
          {groupedTechnicalQuestions.High.length > 0 && (
            <div className="priority-section priority-high">
              <h3>High Priority Questions</h3>
              <div className="questions-list">
                {groupedTechnicalQuestions.High.map((question, index) => (
                  <div className="question-item" key={`tech-high-${index}`}>
                    <div className="question-number">{index + 1}</div>
                    <div className="question-content">
                      <div className="question-text">{question.question}</div>
                      {question.explanation && (
                        <div className="question-explanation">{question.explanation}</div>
                      )}
                      {question.answer && (
                        <div className="question-answer">
                          <strong>Ideal Answer:</strong> {question.answer}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Medium Priority Questions */}
          {groupedTechnicalQuestions.Medium.length > 0 && (
            <div className="priority-section priority-medium">
              <h3>Medium Priority Questions</h3>
              <div className="questions-list">
                {groupedTechnicalQuestions.Medium.map((question, index) => (
                  <div className="question-item" key={`tech-med-${index}`}>
                    <div className="question-number">{index + 1}</div>
                    <div className="question-content">
                      <div className="question-text">{question.question}</div>
                      {question.explanation && (
                        <div className="question-explanation">{question.explanation}</div>
                      )}
                      {question.answer && (
                        <div className="question-answer">
                          <strong>Ideal Answer:</strong> {question.answer}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Low Priority Questions */}
          {groupedTechnicalQuestions.Low.length > 0 && (
            <div className="priority-section priority-low">
              <h3>Low Priority Questions</h3>
              <div className="questions-list">
                {groupedTechnicalQuestions.Low.map((question, index) => (
                  <div className="question-item" key={`tech-low-${index}`}>
                    <div className="question-number">{index + 1}</div>
                    <div className="question-content">
                      <div className="question-text">{question.question}</div>
                      {question.explanation && (
                        <div className="question-explanation">{question.explanation}</div>
                      )}
                      {question.answer && (
                        <div className="question-answer">
                          <strong>Ideal Answer:</strong> {question.answer}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Unspecified Priority Questions (fallback) */}
          {groupedTechnicalQuestions.Unspecified.length > 0 && (
            <div className="questions-list">
              {groupedTechnicalQuestions.Unspecified.map((question, index) => (
                <div className="question-item" key={`tech-unspec-${index}`}>
                  <div className="question-number">{index + 1}</div>
                  <div className="question-content">
                    <div className="question-text">{question.question}</div>
                    {question.explanation && (
                      <div className="question-explanation">{question.explanation}</div>
                    )}
                    {question.answer && (
                      <div className="question-answer">
                        <strong>Ideal Answer:</strong> {question.answer}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        
        {/* Behavioral Questions Section */}
        {behavioralQuestions && behavioralQuestions.length > 0 && (
          <section className="question-section behavioral-questions">
            <h2>Behavioral Interview Questions</h2>
            <div className="questions-list">
              {behavioralQuestions.map((question, index) => (
                <div className="question-item" key={`behavioral-${index}`}>
                  <div className="question-number">{index + 1}</div>
                  <div className="question-content">
                    <div className="question-text">{question.question}</div>
                    {question.explanation && (
                      <div className="question-explanation">{question.explanation}</div>
                    )}
                    {question.answer && (
                      <div className="question-answer">
                        <strong>Ideal Answer:</strong> {question.answer}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Evaluation Guidance Section */}
        {evaluationGuidance && (
          <section className="evaluation-guidance">
            <h2>Evaluation Guidance</h2>
            <div className="guidance-content">
              {Array.isArray(evaluationGuidance) ? (
                <ul className="guidance-list">
                  {evaluationGuidance.map((item, index) => (
                    <li key={`guide-${index}`}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>{evaluationGuidance}</p>
              )}
            </div>
          </section>
        )}
      </div>
    );
  }
  
  // Fallback to markdown rendering if no structured data is available
  return <CustomMarkdownRenderer markdownContent={evaluationData} />;
};

export default TAGScreeningRenderer;